import { NextResponse } from "next/server"
import { z } from "zod"
import { getSession } from "@/services/auth-service"
import {
  getProductReviewsFeed,
  submitProductReview,
} from "@/services/product-review-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

const productParamsSchema = z.object({
  id: z
    .string()
    .regex(/^[1-9]\d*$/, "Product id must be a positive integer.")
    .transform((value) => Number(value)),
})

const createReviewSchema = z.object({
  rating: z.union([
    z.literal(1),
    z.literal(2),
    z.literal(3),
    z.literal(4),
    z.literal(5),
  ]),
  content: z.string().trim().min(1),
  attachments: z
    .array(
      z.object({
        id: z.number(),
        key: z.string(),
        nonce: z.string(),
        url: z.string(),
        type: z.enum(["image", "video"]),
      })
    )
    .optional(),
})

const toReviewsFetchFailedResponse = () =>
  NextResponse.json(
    {
      ok: false,
      code: "PRODUCT_REVIEWS_FETCH_FAILED",
      message: "Nao foi possivel carregar as avaliacoes do produto.",
    },
    {
      status: 502,
      headers: NO_CACHE_HEADERS,
    }
  )

const toReviewSubmitFailedResponse = () =>
  NextResponse.json(
    {
      ok: false,
      code: "PRODUCT_REVIEW_SUBMIT_FAILED",
      message: "Nao foi possivel enviar a avaliacao do produto.",
    },
    {
      status: 502,
      headers: NO_CACHE_HEADERS,
    }
  )

const toSubmitStatus = (
  result: Awaited<ReturnType<typeof submitProductReview>>
): number => {
  if (result.ok) {
    return 200
  }

  if (result.code === "REVIEW_NOT_ALLOWED") {
    return 400
  }

  return 502
}

const parseRequestBody = async (request: Request): Promise<unknown> => {
  const rawBody = await request.text()

  if (!rawBody.trim()) {
    return {}
  }

  try {
    return JSON.parse(rawBody) as unknown
  } catch {
    return null
  }
}

export async function GET(
  _: Request,
  context: { params: Promise<{ id: string }> }
) {
  const params = await context.params
  const validation = productParamsSchema.safeParse(params)

  if (!validation.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PARAMS",
        message:
          validation.error.issues[0]?.message ??
          "Product id must be a positive integer.",
      },
      {
        status: 400,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  try {
    const session = await getSession()
    const feed = await getProductReviewsFeed(validation.data.id, {
      currentUserEmail: session?.user?.email,
    })

    return NextResponse.json(feed, {
      headers: NO_CACHE_HEADERS,
    })
  } catch {
    return toReviewsFetchFailedResponse()
  }
}

export async function POST(
  request: Request,
  context: { params: Promise<{ id: string }> }
) {
  const params = await context.params
  const paramsValidation = productParamsSchema.safeParse(params)

  if (!paramsValidation.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PARAMS",
        message:
          paramsValidation.error.issues[0]?.message ??
          "Product id must be a positive integer.",
      },
      {
        status: 400,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  const session = await getSession()
  const sessionCookie = session?.wordpressSessionCookie?.trim()

  if (!(session && sessionCookie && session.user)) {
    return NextResponse.json(
      {
        ok: false,
        code: "UNAUTHORIZED",
        message: "Sessao nao autenticada.",
      },
      {
        status: 401,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  const parsedBody = createReviewSchema.safeParse(
    await parseRequestBody(request)
  )

  if (!parsedBody.success) {
    return NextResponse.json(
      {
        ok: false,
        code: "INVALID_PAYLOAD",
        message: "Payload invalido para criar avaliacao.",
      },
      {
        status: 400,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  try {
    const result = await submitProductReview({
      productId: paramsValidation.data.id,
      sessionCookie,
      currentUser: session.user,
      rating: parsedBody.data.rating,
      content: parsedBody.data.content,
      attachments: parsedBody.data.attachments,
    })

    return NextResponse.json(result, {
      status: toSubmitStatus(result),
      headers: NO_CACHE_HEADERS,
    })
  } catch {
    return toReviewSubmitFailedResponse()
  }
}
